import React, { useState, useEffect } from 'react';
import { useData } from '../hooks/useData';

const DataContextTest = () => {
  const { 
    users, 
    addUser, 
    updateUserProfile, 
    updateUserKYCStatus, 
    updateUserAccountStatus,
    updateUserInvestmentData,
    detectFraudAlerts,
    addUserFraudAlerts
  } = useData();
  
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addTestResult = (result) => {
    setTestResults(prev => [...prev, result]);
  };

  const runTests = async () => {
    setLoading(true);
    setTestResults([]);
    
    try {
      // Test 1: Add user
      addTestResult('Test 1: Adding user...');
      const newUser = {
        name: 'Test User',
        email: 'test@example.com',
        phone: '+1234567890',
        joinDate: new Date().toISOString().split('T')[0],
        kycStatus: 'pending',
        status: 'active',
        totalInvestment: 0,
        currentROI: 0
      };
      
      const addedUser = await addUser(newUser);
      addTestResult(`✅ Test 1 PASSED: User added with ID ${addedUser.id}`);
      
      // Test 2: Update user profile
      addTestResult('Test 2: Updating user profile...');
      const updatedUser = await updateUserProfile(addedUser.id, {
        name: 'Updated Test User',
        email: 'updated@example.com'
      });
      addTestResult(`✅ Test 2 PASSED: User profile updated`);
      
      // Test 3: Update KYC status
      addTestResult('Test 3: Updating KYC status...');
      await updateUserKYCStatus(addedUser.id, 'approved');
      addTestResult(`✅ Test 3 PASSED: KYC status updated to approved`);
      
      // Test 4: Update account status
      addTestResult('Test 4: Updating account status...');
      await updateUserAccountStatus(addedUser.id, 'suspended');
      addTestResult(`✅ Test 4 PASSED: Account status updated to suspended`);
      
      // Test 5: Update investment data
      addTestResult('Test 5: Updating investment data...');
      await updateUserInvestmentData(addedUser.id, {
        totalInvestment: 50000,
        currentROI: 2500
      });
      addTestResult(`✅ Test 5 PASSED: Investment data updated`);
      
      // Test 6: Detect fraud alerts
      addTestResult('Test 6: Detecting fraud alerts...');
      const fraudAlerts = detectFraudAlerts({
        ...updatedUser,
        totalInvestment: 50000,
        joinDate: new Date().toISOString().split('T')[0], // New account
        kycStatus: 'rejected',
        status: 'active'
      });
      addTestResult(`✅ Test 6 PASSED: Detected ${fraudAlerts.length} fraud alerts`);
      
      // Test 7: Add fraud alerts
      addTestResult('Test 7: Adding fraud alerts...');
      await addUserFraudAlerts(addedUser.id, fraudAlerts);
      addTestResult(`✅ Test 7 PASSED: Added ${fraudAlerts.length} fraud alerts to user`);
      
      addTestResult('🎉 All tests completed successfully!');
    } catch (error) {
      addTestResult(`❌ Test FAILED: ${error.message}`);
      console.error('Test error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">DataContext Test</h1>
      
      <div className="mb-6">
        <button
          onClick={runTests}
          disabled={loading}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Running Tests...' : 'Run Tests'}
        </button>
      </div>
      
      <div className="bg-gray-100 p-4 rounded mb-6">
        <h2 className="font-semibold mb-2">Users Count: {users.length}</h2>
      </div>
      
      <div className="bg-gray-100 p-4 rounded">
        <h2 className="font-semibold mb-2">Test Results:</h2>
        <div className="space-y-2">
          {testResults.map((result, index) => (
            <div key={index} className="font-mono text-sm">
              {result}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DataContextTest;